import { useRoutes } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Page from './components/Page';
import { Fragment } from 'react';
import RouterGuard from './store/RouterGuard';
import ArqueiroBootstrap from './components/ArqueiroBootstrap';
import Modal from './components/Modal';
import useStore from './components/Hooks/useStore';
import { SnackbarProvider } from 'notistack';
import Session from './components/Session';
import Sidebar from './components/Sidebar';
import { AbilityProvider } from './provider/AbilityProvider';
import Controls from './components/Controls';
import Breadchumb from './components/Breadchumb';
import { ExportProvider } from './provider/ExportProvider';
import { BannerProvider } from './provider/BannerProvider';
import { ValidityProvider } from './provider/ValidityProvider';

const withHeader = (route) => {
  if (Array.isArray(route)) {
    return route.map(withHeader);
  }

  if (route.children) {
    return {
      ...route,
      children: route.children.map(withHeader),
    };
  }

  if (route.element) {
    return {
      ...route,
      element: (
        <Fragment>
          <Header />
          <div
            style={{
              display: 'flex',
            }}
          >
            <Breadchumb.Router />
            <Controls />
          </div>
          {route.element}
        </Fragment>
      ),
    };
  }

  return route;
};

export const routes = [
  {
    path: '/',
    children: [
      {
        index: true,
        element: <Page.Home />,
      },
      {
        path: 'recover',
        element: <Page.Recover />,
      },
      {
        path: 'tools',
        children: [
          {
            index: true,
            element: (
              <Fragment>
                <Breadchumb.Router />
                <Page.Tools />
              </Fragment>
            ),
          },
          ...withHeader([
            {
              path: 'price',
              element: <Page.Tools.Price />,
            },
            {
              path: 'banner',
              children: [
                {
                  index: true,
                  element: (
                    <BannerProvider>
                      <Page.Banner />
                    </BannerProvider>
                  ),
                },
                {
                  path: 'promotions',
                  element: (
                    <BannerProvider>
                      <Page.Banner.Promotions />,
                    </BannerProvider>
                  ),
                },
                {
                  path: 'files',
                  element: (
                    <BannerProvider>
                      <Page.Banner.Files />
                    </BannerProvider>
                  ),
                },
                {
                  path: 'layouts',
                  element: (
                    <BannerProvider>
                      <Page.Banner.Layouts />
                    </BannerProvider>
                  ),
                },
                {
                  path: 'configurations',
                  element: (
                    <BannerProvider>
                      <Page.Banner.Configurations />
                    </BannerProvider>
                  ),
                },
              ],
            },
            {
              path: 'ticket',
              children: [
                {
                  index: true,
                  element: (
                    <BannerProvider>
                      <Page.Ticket />
                    </BannerProvider>
                  ),
                },
                {
                  path: 'offline-prices',
                  element: (
                    <BannerProvider>
                      <Page.Ticket.OffilineProducts />
                    </BannerProvider>
                  ),
                },
              ],
            },
          ]),
        ],
      },
      {
        path: 'tools/exports',
        children: [
          {
            index: true,
            element: (
              <ExportProvider>
                <Sidebar.Export>
                  <Page.Exports />
                </Sidebar.Export>
              </ExportProvider>
            ),
          },
        ],
      },
      {
        path: 'tools/exports/invoices',
        children: [
          {
            index: true,
            element: (
              <ExportProvider>
                <Sidebar.Export>
                  <Page.Exports.Invoices />
                </Sidebar.Export>
              </ExportProvider>
            ),
          },
        ],
      },
      {
        path: 'tools/exports/taxes',
        children: [
          {
            index: true,
            element: (
              <ExportProvider>
                <Sidebar.Export>
                  <Page.Exports.Taxes />
                </Sidebar.Export>
              </ExportProvider>
            ),
          },
        ],
      },
      {
        path: 'tools/exports/companies',
        children: [
          {
            index: true,
            element: (
              <ExportProvider>
                <Sidebar.Export>
                  <Page.Exports.Companies />
                </Sidebar.Export>
              </ExportProvider>
            ),
          },
        ],
      },

      {
        path: 'tools/exports/deposits',
        children: [
          {
            index: true,
            element: (
              <ExportProvider>
                <Sidebar.Export>
                  <Page.Exports.Deposits />
                </Sidebar.Export>
              </ExportProvider>
            ),
          },
        ],
      },
      {
        path: 'tools/exports/logistic',
        children: [
          {
            index: true,
            element: (
              <ExportProvider>
                <Sidebar.Export>
                  <Page.Exports.Logistic />
                </Sidebar.Export>
              </ExportProvider>
            ),
          },
        ],
      },
      {
        path: 'tools/validity',
        children: [
          {
            index: true,
            element: (
              <ValidityProvider>
                <Sidebar.Validity>
                  <Page.Validity />
                </Sidebar.Validity>
              </ValidityProvider>
            ),
          },
        ],
      },
      {
        path: 'tools/validity/validities',
        children: [
          {
            index: true,
            element: (
              <ValidityProvider>
                <Sidebar.Validity>
                  <Page.Validity.Validities />
                </Sidebar.Validity>
              </ValidityProvider>
            ),
          },
        ],
      },

      {
        path: 'tools/validity/status',
        children: [
          {
            index: true,
            element: (
              <ValidityProvider>
                <Sidebar.Validity>
                  <Page.Validity.Status />
                </Sidebar.Validity>
              </ValidityProvider>
            ),
          },
        ],
      },
    ],
  },
];

function App() {
  const [getters] = useStore();

  let pages = useRoutes(routes);
  const {
    modal: { getModal },
  } = getters;

  return (
    <SnackbarProvider
      maxSnack={2}
      style={{
        fontSize: 20,
        borderRadius: 10,
      }}
      autoHideDuration={2000}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <RouterGuard>
        <Session>
          <AbilityProvider>
            <ArqueiroBootstrap />
            {pages}
            {getModal('single.printBannerForm') ? <Modal.PrintBanners /> : null}
            {getModal('single.confirmBannerFormsDeletion') ? (
              <Modal.ConfirmBannerFormsDeletion />
            ) : null}
            {getModal('single.recoverPassword') ? (
              <Modal.RecoverPassword />
            ) : null}
          </AbilityProvider>
        </Session>
      </RouterGuard>
    </SnackbarProvider>
  );
}

export default App;
